import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UserCircleIcon, MailIcon, PhoneIcon, MapPinIcon, GlobeIcon, UsersIcon, DollarSignIcon, BriefcaseIcon, LinkedinIcon, CalendarIcon, CpuIcon, ShareIcon, FileTextIcon, AwardIcon, TargetIcon, ClipboardListIcon, MapIcon, ClockIcon, StarIcon, TrendingUpIcon, BuildingIcon, ShieldCheckIcon, TagIcon, HashIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
const coreFields = [{
  name: 'Owner / Partner Name',
  description: 'First and last name of managing partners and owners',
  icon: UserCircleIcon
}, {
  name: 'Verified Email Address',
  description: 'Direct B2B email addresses verified in real-time',
  icon: MailIcon
}, {
  name: 'Direct Phone Number',
  description: 'Office and direct dial numbers for decision-makers',
  icon: PhoneIcon
}, {
  name: 'Job Title',
  description: 'Exact titles like CPA, Managing Partner, Tax Director',
  icon: BriefcaseIcon
}, {
  name: 'Full Business Address',
  description: 'Complete street address, city, state, and ZIP code',
  icon: MapPinIcon
}, {
  name: 'Company Website',
  description: 'Domain names and online presence information',
  icon: GlobeIcon
}, {
  name: 'Employee Count',
  description: 'Number of employees to filter by firm size',
  icon: UsersIcon
}, {
  name: 'Estimated Revenue',
  description: 'Annual revenue ranges for targeting high-value firms',
  icon: DollarSignIcon
}];
const additionalFields = [{
  name: 'Specialization',
  description: 'CPA, tax prep, bookkeeping, audit, forensic accounting',
  icon: ClipboardListIcon
}, {
  name: 'LinkedIn Profiles',
  description: 'Professional LinkedIn URLs for owners and key staff',
  icon: LinkedinIcon
}, {
  name: 'Years in Business',
  description: 'Founding year and business longevity data',
  icon: CalendarIcon
}, {
  name: 'Technology Stack',
  description: 'Software used (QuickBooks, Xero, Sage, etc.)',
  icon: CpuIcon
}, {
  name: 'Social Media Profiles',
  description: 'Facebook, Twitter, Instagram business accounts',
  icon: ShareIcon
}, {
  name: 'Industry Codes',
  description: 'NAICS and SIC codes for precise targeting',
  icon: HashIcon
}, {
  name: 'Certifications',
  description: 'CPA licenses, state certifications, specializations',
  icon: AwardIcon
}, {
  name: 'Client Size Focus',
  description: 'Whether they serve individuals, SMBs, or enterprises',
  icon: TargetIcon
}, {
  name: 'Service Offerings',
  description: 'Tax, audit, consulting, payroll, advisory services',
  icon: FileTextIcon
}, {
  name: 'Office Locations',
  description: 'Multiple branch addresses and regional presence',
  icon: MapIcon
}, {
  name: 'Business Hours',
  description: 'Operating hours and availability information',
  icon: ClockIcon
}, {
  name: 'Reviews & Ratings',
  description: 'Google, Yelp, and industry review scores',
  icon: StarIcon
}, {
  name: 'Growth Indicators',
  description: 'Hiring trends, expansion signals, funding events',
  icon: TrendingUpIcon
}, {
  name: 'Business Structure',
  description: 'LLC, Corporation, Partnership, Sole Proprietor',
  icon: BuildingIcon
}, {
  name: 'Compliance Status',
  description: 'License status, regulatory standing, accreditations',
  icon: ShieldCheckIcon
}, {
  name: 'Industry Focus',
  description: 'Verticals served (healthcare, real estate, tech, etc.)',
  icon: TagIcon
}];
const container = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05
    }
  }
};
const item = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0
  }
};
export function AccountingFields() {
  const [showAll, setShowAll] = useState(false);
  return <section className="py-24 bg-slate-50" data-id="element-355">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-356">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-357">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 leading-tight" data-id="element-358">
            What's included in the accounting database?
          </h2>
          <p className="text-xl text-slate-600" data-id="element-359">
            Our accounting owner contacts are enriched with{' '}
            <strong className="text-blue-600" data-id="element-360">45+ data points</strong> to help
            you segment, personalize, and convert your ideal prospects.
          </p>
        </div>

        {/* Core Fields - Always Visible */}
        <motion.div variants={container} initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: '-100px'
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-361">
          {coreFields.map(field => <motion.div key={field.name} variants={item} whileHover={{
          y: -4,
          transition: {
            duration: 0.2
          }
        }} className="p-6 rounded-2xl bg-white border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all group" data-id="element-362">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors" data-id="element-363">
                <field.icon className="w-6 h-6" data-id="element-364" />
              </div>
              <h3 className="text-base font-bold text-slate-900 mb-2" data-id="element-365">
                {field.name}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed" data-id="element-366">
                {field.description}
              </p>
            </motion.div>)}
        </motion.div>

        {/* Additional Fields - Expandable */}
        <AnimatePresence data-id="element-367">
          {showAll && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.3
        }} className="overflow-hidden" data-id="element-368">
              <motion.div variants={container} initial="hidden" animate="show" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-369">
                {additionalFields.map(field => <motion.div key={field.name} variants={item} whileHover={{
              y: -4,
              transition: {
                duration: 0.2
              }
            }} className="p-6 rounded-2xl bg-white border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all group" data-id="element-370">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors" data-id="element-371">
                      <field.icon className="w-6 h-6" data-id="element-372" />
                    </div>
                    <h3 className="text-base font-bold text-slate-900 mb-2" data-id="element-373">
                      {field.name}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed" data-id="element-374">
                      {field.description}
                    </p>
                  </motion.div>)}
              </motion.div>
            </motion.div>}
        </AnimatePresence>

        {/* Expand/Collapse Button */}
        <div className="text-center" data-id="element-375">
          <button onClick={() => setShowAll(!showAll)} className="inline-flex items-center gap-2 px-6 py-3 bg-white border-2 border-blue-600 text-blue-600 font-semibold rounded-full hover:bg-blue-600 hover:text-white transition-all" data-id="element-376">
            {showAll ? <>
                Show less
                <ChevronUpIcon className="w-5 h-5" data-id="element-377" />
              </> : <>
                View all 45+ data points
                <ChevronDownIcon className="w-5 h-5" data-id="element-378" />
              </>}
          </button>
        </div>
      </div>
    </section>;
}